import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, useParams, useNavigate, Link } from "react-router-dom";
import * as api from "./api";

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/s/:storeId" element={<GuardPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/store/:storeId" element={<AdminStorePage />} />
        <Route path="/admin/store/:storeId/:day" element={<AdminDayPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </BrowserRouter>
  );
}

const C = {
  bg: "#F9FAFB", surface: "#FFFFFF", border: "#E5E7EB", borderLight: "#F3F4F6",
  text: "#111827", textMid: "#374151", textSub: "#6B7280", textMute: "#9CA3AF",
  accent: "#2563EB", accentLight: "#EFF6FF", accentMid: "#DBEAFE",
  green: "#059669", greenLight: "#ECFDF5", red: "#DC2626", redLight: "#FEF2F2",
  amber: "#D97706", amberLight: "#FFFBEB",
};
const T = {
  h1: { fontSize: 22, fontWeight: 700, color: C.text, margin: 0 },
  h2: { fontSize: 18, fontWeight: 600, color: C.text, margin: 0 },
  h3: { fontSize: 15, fontWeight: 600, color: C.text, margin: 0 },
  body: { fontSize: 14, color: C.textMid },
  small: { fontSize: 12, color: C.textSub },
  label: { fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: "uppercase", letterSpacing: ".6px" },
};
const font = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif";

function Shell({ children, bg = C.bg }) {
  return <div style={{ minHeight: "100vh", background: bg, fontFamily: font, color: C.text }}>{children}</div>;
}
function Card({ children, style: s, pad = 24 }) {
  return <div style={{ background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, padding: pad, ...s }}>{children}</div>;
}
function Btn({ children, onClick, variant = "primary", size = "md", disabled, fullWidth, style: s }) {
  const base = { border: "none", borderRadius: 8, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, transition: "opacity .15s", opacity: disabled ? .5 : 1, fontFamily: font, ...s };
  const sizes = { sm: { padding: "6px 12px", fontSize: 12 }, md: { padding: "9px 18px", fontSize: 14 }, lg: { padding: "12px 24px", fontSize: 15 } };
  const variants = { primary: { background: C.accent, color: "#fff" }, danger: { background: C.red, color: "#fff" }, ghost: { background: "transparent", color: C.textMid, border: `1px solid ${C.border}` }, subtle: { background: C.bg, color: C.textMid } };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...sizes[size], ...variants[variant], width: fullWidth ? "100%" : undefined }}>{children}</button>;
}
function Input({ label, value, onChange, placeholder, type = "text", maxLength, style: s }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ ...T.label, display: "block", marginBottom: 6 }}>{label}</label>}
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} maxLength={maxLength}
        style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box", fontFamily: font, background: C.surface, ...s }} />
    </div>
  );
}
function Badge({ children, color = "green" }) {
  const map = { green: { bg: C.greenLight, text: C.green }, red: { bg: C.redLight, text: C.red }, amber: { bg: C.amberLight, text: C.amber }, blue: { bg: C.accentMid, text: C.accent } };
  const c = map[color] || map.green;
  return <span style={{ background: c.bg, color: c.text, fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 20 }}>{children}</span>;
}
function Divider() { return <div style={{ height: 1, background: C.borderLight, margin: "0 -24px" }} />; }
function Loader({ text = "Loading..." }) {
  return (
    <div style={{ textAlign: "center", padding: 60, color: C.textMute }}>
      <div style={{ width: 28, height: 28, border: `2px solid ${C.border}`, borderTopColor: C.accent, borderRadius: "50%", margin: "0 auto 12px", animation: "spin 1s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <p style={{ ...T.small, margin: 0 }}>{text}</p>
    </div>
  );
}
function Spinner() {
  return (
    <>
      <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTopColor: C.accent, borderRadius: "50%", margin: "20px auto", animation: "spin 1s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}
function EmptyState({ text }) { return <div style={{ textAlign: "center", padding: "40px 20px", color: C.textMute, fontSize: 14 }}>{text}</div>; }
function InfoRow({ label, value, valueColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
      <span style={{ color: C.textSub }}>{label}</span>
      <span style={{ fontWeight: 600, color: valueColor || C.text }}>{value}</span>
    </div>
  );
}
function SearchBar({ value, onChange, placeholder = "Search..." }) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: "100%", padding: "9px 14px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box", fontFamily: font, background: C.surface, marginBottom: 16 }} />
  );
}

function LandingPage() {
  return (
    <Shell bg={C.surface}>
      <div style={{ maxWidth: 400, margin: "0 auto", padding: "80px 24px", textAlign: "center" }}>
        <img src="/logo.jpeg" alt="Zippee" style={{ height: 48, width: "auto", objectFit: "contain", marginBottom: 20 }} />
        <p style={{ ...T.body, color: C.textSub, marginBottom: 28, lineHeight: 1.6 }}>Security guard attendance management system</p>
        <Link to="/admin" style={{ padding: "10px 22px", background: C.accent, color: "#fff", borderRadius: 8, fontWeight: 600, fontSize: 14, textDecoration: "none", display: "inline-block", fontFamily: font }}>Open Admin Dashboard →</Link>
      </div>
    </Shell>
  );
}

function GuardPage() {
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("name");
  const [guardName, setGuardName] = useState("");
  const [guardMobile, setGuardMobile] = useState("");
  const [selfieData, setSelfieData] = useState(null);
  const [result, setResult] = useState(null);
  const [blockInfo, setBlockInfo] = useState(null);
  const [checkinRecord, setCheckinRecord] = useState(null);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    api.getStore(storeId).then(async (s) => {
      setStore(s); setLoading(false);
      try {
        const saved = JSON.parse(localStorage.getItem("guardtrack_guard") || "{}");
        const today = new Date().toISOString().slice(0, 10);
        if (saved.date === today && saved.store === storeId && saved.name) {
          setGuardName(saved.name); setGuardMobile(saved.mobile || "");
          try {
            const { record } = await api.getCheckinStatus(storeId, saved.name);
            if (record) {
              setCheckinRecord(record);
              if (record.checkout_time) { setCheckoutResult({ checkout_time: record.checkout_time, working_hours: record.working_hours, checkout_type: record.checkout_type }); setStep("checked_out"); }
              else setStep("checkout");
            }
          } catch {}
        } else localStorage.removeItem("guardtrack_guard");
      } catch { localStorage.removeItem("guardtrack_guard"); }
    }).catch(() => { setError("Store not found"); setLoading(false); });
  }, [storeId]);

  const stopCamera = useCallback(() => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } }, []);
  const startCamera = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 480 } } });
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); }
    } catch { setStep("name"); }
  }, []);
  useEffect(() => { if (step === "selfie") startCamera(); else stopCamera(); return stopCamera; }, [step, startCamera, stopCamera]);

  const captureSelfie = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    const scale = Math.min(480 / v.videoWidth, 480 / v.videoHeight, 1);
    c.width = v.videoWidth * scale; c.height = v.videoHeight * scale;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const data = c.toDataURL("image/jpeg", 0.6);
    setSelfieData(data); stopCamera(); setStep("verifying");
    submitAttendance(data);
  };

  const submitAttendance = (selfie) => {
    if (!navigator.geolocation) { setStep("gps_error"); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const res = await api.markAttendance({ guard_name: guardName, guard_mobile: guardMobile, location_id: storeId, lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy), selfie });
        localStorage.setItem("guardtrack_guard", JSON.stringify({ name: guardName, mobile: guardMobile, date: new Date().toISOString().slice(0, 10), store: storeId }));
        setResult(res); setStep("done");
        setTimeout(async () => { try { const { record } = await api.getCheckinStatus(storeId, guardName); if (record) setCheckinRecord(record); } catch {} }, 800);
      } catch (e) {
        if (e.status === 403 && e.data) setBlockInfo(e.data);
        else setBlockInfo({ distance: null, radius: store?.radius || 100, message: e.message || "Could not verify location" });
        setStep("blocked");
      }
    }, () => setStep("gps_error"), { enableHighAccuracy: true, timeout: 15000 });
  };

  const [checkoutError, setCheckoutError] = useState(null);
  const handleCheckout = async () => {
    setCheckoutError(null); setCheckingOut(true);
    try {
      const res = await api.checkOut({ guard_name: guardName, location_id: storeId });
      setCheckoutResult(res); setStep("checked_out");
    } catch (e) { setCheckoutError(e.message || "Checkout failed"); }
    setCheckingOut(false);
  };

  const reset = () => {
    setStep("name"); setGuardName(""); setGuardMobile(""); setSelfieData(null);
    setResult(null); setBlockInfo(null); setCheckinRecord(null); setCheckoutResult(null);
    localStorage.removeItem("guardtrack_guard");
  };

  if (loading) return <Shell><Loader text="Loading store..." /></Shell>;
  if (error) return <Shell><div style={{ maxWidth: 400, margin: "80px auto", padding: 20 }}><Card><p style={{ ...T.body, textAlign: "center", color: C.red }}>{error}</p></Card></div></Shell>;

  return (
    <Shell bg={C.bg}>
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/logo.jpeg" alt="Zippee" style={{ height: 32, width: "auto", objectFit: "contain" }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{store.name}</div>
            <div style={{ fontSize: 11, color: C.textMute }}>{store.address}</div>
          </div>
        </div>
        <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "6px 10px", textAlign: "center" }}>
          <div style={{ fontSize: 9, color: C.amber, fontWeight: 700, letterSpacing: ".5px" }}>EMERGENCY</div>
          <a href="tel:9818255272" style={{ display: "block", fontSize: 12, color: C.amber, fontWeight: 700, textDecoration: "none" }}>9818255272</a>
          <a href="tel:+911244430029" style={{ display: "block", fontSize: 12, color: C.amber, fontWeight: 700, textDecoration: "none" }}>+911244430029</a>
        </div>
      </div>

      <div style={{ maxWidth: 420, margin: "0 auto", padding: "24px 16px" }}>
        {step === "name" && (
          <Card>
            <h2 style={{ ...T.h2, marginBottom: 4 }}>Mark Attendance</h2>
            <p style={{ ...T.small, marginBottom: 20 }}>Enter your details to check in</p>
            <Input label="Full Name" value={guardName} onChange={setGuardName} placeholder="Rajesh Kumar" />
            <Input label="Mobile Number" value={guardMobile} onChange={setGuardMobile} placeholder="9818255272" type="tel" maxLength={10} />
            <Btn fullWidth disabled={!guardName.trim() || !guardMobile.trim()}
              onClick={async () => {
                const name = guardName.trim(), mobile = guardMobile.trim();
                if (!name || !mobile) return;
                setGuardName(name); setGuardMobile(mobile);
                try {
                  const { record } = await api.getCheckinStatus(storeId, name);
                  if (record) {
                    setCheckinRecord(record);
                    if (record.checkout_time) { setCheckoutResult({ checkout_time: record.checkout_time, working_hours: record.working_hours, checkout_type: record.checkout_type }); setStep("checked_out"); }
                    else setStep("checkout");
                  } else setStep("selfie");
                } catch { setStep("selfie"); }
              }}>Continue →</Btn>
          </Card>
        )}
        {step === "selfie" && (
          <Card>
            <button onClick={() => setStep("name")} style={{ background: "none", border: "none", color: C.accent, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 14 }}>← Back</button>
            <h2 style={{ ...T.h2, marginBottom: 4 }}>Take a Selfie</h2>
            <p style={{ ...T.small, marginBottom: 16 }}>{guardName} · {store.name}</p>
            <div style={{ background: "#000", borderRadius: 10, overflow: "hidden", width: "100%", aspectRatio: "1/1", marginBottom: 14 }}>
              <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
            </div>
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <Btn fullWidth onClick={captureSelfie}>Capture & Verify Location</Btn>
          </Card>
        )}
        {step === "verifying" && (
          <Card style={{ textAlign: "center" }}>
            {selfieData && <img src={selfieData} alt="" style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: `2px solid ${C.border}`, marginBottom: 12 }} />}
            <p style={{ ...T.h3, marginBottom: 4 }}>{guardName}</p>
            <Spinner />
            <p style={{ ...T.small }}>Verifying your location…</p>
          </Card>
        )}
        {step === "done" && result && (
          <Card>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, background: C.greenLight, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><CheckIcon size={22} color={C.green} /></div>
              <h2 style={{ ...T.h2, marginBottom: 4 }}>Checked In</h2>
              <p style={{ ...T.small }}>{new Date(result.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} · {result.location_name}</p>
            </div>
            <Divider />
            <div style={{ paddingTop: 16 }}>
              <p style={{ ...T.small, marginBottom: 10, textAlign: "center" }}>Tap below when your shift ends</p>
              <Btn fullWidth variant="ghost" onClick={() => { setCheckinRecord({ timestamp: result.timestamp }); setStep("checkout"); }}>Check Out</Btn>
            </div>
          </Card>
        )}
        {step === "checkout" && checkinRecord && (
          <Card>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, background: C.accentMid, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><ClockIcon size={22} color={C.accent} /></div>
              <h2 style={{ ...T.h2, marginBottom: 4 }}>End Shift</h2>
              <p style={{ ...T.small }}>{guardName} · {store.name}</p>
            </div>
            <Divider />
            <div style={{ padding: "14px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: C.textSub }}>Checked in at</span>
                <span style={{ fontWeight: 600, color: C.green }}>{new Date(checkinRecord.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            </div>
            <Divider />
            <div style={{ paddingTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
              {checkoutError && <p style={{ color: C.red, fontSize: 13, textAlign: "center", background: C.redLight, borderRadius: 8, padding: "10px 14px", margin: 0 }}>{checkoutError}</p>}
              <Btn fullWidth variant="danger" disabled={checkingOut} onClick={handleCheckout}>{checkingOut ? "Processing…" : "Confirm Check Out"}</Btn>
              <p style={{ ...T.small, textAlign: "center", cursor: "pointer", color: C.accent }} onClick={reset}>Not {guardName}? Switch guard</p>
            </div>
          </Card>
        )}
        {step === "checked_out" && checkoutResult && (
          <Card>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, background: C.greenLight, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><CheckIcon size={22} color={C.green} /></div>
              <h2 style={{ ...T.h2, marginBottom: 4 }}>Shift Complete</h2>
              <p style={{ ...T.small }}>{guardName} · {store.name}</p>
            </div>
            <Divider />
            <div style={{ padding: "14px 0", display: "flex", flexDirection: "column", gap: 10 }}>
              {checkinRecord && <InfoRow label="Check-in" value={new Date(checkinRecord.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} />}
              <InfoRow label="Check-out" value={new Date(checkoutResult.checkout_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} valueColor={C.green} />
              <InfoRow label="Hours Worked" value={`${checkoutResult.working_hours} hrs`} valueColor={C.accent} />
            </div>
          </Card>
        )}
        {step === "blocked" && (
          <Card>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, background: C.redLight, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><XIcon size={22} color={C.red} /></div>
              <h2 style={{ ...T.h2, marginBottom: 4 }}>Outside Geofence</h2>
              <p style={{ ...T.small }}>You must be within {blockInfo?.radius || store.radius}m of {store.name}</p>
            </div>
            <Divider />
            <div style={{ padding: "14px 0", display: "flex", flexDirection: "column", gap: 8 }}>
              {blockInfo?.distance != null && <InfoRow label="Your distance" value={`${blockInfo.distance}m`} valueColor={C.red} />}
              <InfoRow label="Required" value={`Within ${blockInfo?.radius || store.radius}m`} />
            </div>
            <Divider />
            <div style={{ paddingTop: 16 }}><Btn fullWidth onClick={reset}>Try Again</Btn></div>
          </Card>
        )}
        {step === "gps_error" && (
          <Card style={{ textAlign: "center" }}>
            <div style={{ width: 48, height: 48, background: C.amberLight, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><AlertIcon size={22} color={C.amber} /></div>
            <h2 style={{ ...T.h2, marginBottom: 8 }}>Location Access Denied</h2>
            <p style={{ ...T.small, marginBottom: 16 }}>Please enable GPS permissions in your browser and try again.</p>
            <Btn fullWidth onClick={reset}>Try Again</Btn>
          </Card>
        )}
      </div>
    </Shell>
  );
}

function AdminPage() {
  const [authState, setAuthState] = useState(() => {
    const token = localStorage.getItem("guardtrack_token");
    if (!token) return "none";
    try {
      const [ts] = token.split(".");
      if (Date.now() - parseInt(ts) > 86400000) { localStorage.removeItem("guardtrack_token"); return "none"; }
      return "logged_in";
    } catch { return "none"; }
  });
  const [role, setRole] = useState("admin");
  const [tab, setTab] = useState("dashboard");

  if (authState === "none") return (
    <Shell bg={C.bg}>
      <AdminHeader />
      <AdminLogin onLogin={(r) => { setRole(r); setAuthState("logged_in"); }} />
    </Shell>
  );

  return (
    <Shell bg={C.bg}>
      <AdminHeader onLogout={() => { api.adminLogout(); setAuthState("none"); }} />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 40px" }}>
        <AdminTabs tabs={[
          { key: "dashboard",  label: "Dashboard" },
          { key: "attendance", label: "Attendance" },
          { key: "locations",  label: "Locations" },
          { key: "analytics",  label: "Analytics" },
          ...(role === "admin" ? [{ key: "users", label: "User Access" }] : []),
        ]} active={tab} onChange={setTab} />
        {tab === "dashboard"  && <AdminDashboard />}
        {tab === "attendance" && <AdminAttendanceList />}
        {tab === "locations"  && <AdminLocationsList readOnly={role === "store"} />}
        {tab === "analytics"  && <AdminAnalytics />}
        {tab === "users"      && role === "admin" && <AdminUserAccess />}
      </div>
    </Shell>
  );
}

function AdminHeader({ onLogout }) {
  return (
    <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "0 24px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Link to="/admin" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
        <img src="/logo.jpeg" alt="Zippee" style={{ height: 32, width: "auto", objectFit: "contain" }} />
      </Link>
      {onLogout && <Btn variant="ghost" size="sm" onClick={onLogout}>Sign out</Btn>}
    </div>
  );
}

function AdminTabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, marginBottom: 28, marginTop: 4 }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          style={{ padding: "14px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 14, fontWeight: active === t.key ? 600 : 400, color: active === t.key ? C.accent : C.textSub, borderBottom: `2px solid ${active === t.key ? C.accent : "transparent"}`, marginBottom: -1, fontFamily: font, transition: "all .15s" }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function AdminLogin({ onLogin }) {
  const [mode, setMode] = useState("admin");
  const [pass, setPass] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAdmin = async () => { setLoading(true); setErr(""); try { await api.adminLogin(pass); onLogin("admin"); } catch { setErr("Invalid password"); } setLoading(false); };
  const handleUser = async () => { setLoading(true); setErr(""); try { await api.storeUserLogin({ email, password: pass }); onLogin("store"); } catch { setErr("Invalid email or password"); } setLoading(false); };

  return (
    <div style={{ maxWidth: 360, margin: "80px auto", padding: "0 20px" }}>
      <Card>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img src="/logo.jpeg" alt="Zippee" style={{ height: 40, width: "auto", objectFit: "contain", marginBottom: 12 }} />
          <h2 style={{ ...T.h2, marginBottom: 4 }}>Sign in</h2>
          <p style={{ ...T.small }}>GuardTrack Admin</p>
        </div>
        <div style={{ display: "flex", background: C.bg, borderRadius: 8, padding: 3, marginBottom: 20 }}>
          {[["admin","Admin"], ["user","Store User"]].map(([k, label]) => (
            <button key={k} onClick={() => { setMode(k); setErr(""); setPass(""); setEmail(""); }}
              style={{ flex: 1, padding: "7px 0", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 500, fontSize: 13, background: mode === k ? C.surface : "transparent", color: mode === k ? C.text : C.textSub, boxShadow: mode === k ? "0 1px 3px rgba(0,0,0,.08)" : "none", fontFamily: font }}>
              {label}
            </button>
          ))}
        </div>
        {mode === "user" && <Input label="Email" value={email} onChange={setEmail} placeholder="user@company.com" type="email" />}
        <Input label="Password" value={pass} onChange={setPass} placeholder="Enter password" type="password" />
        {err && <p style={{ color: C.red, fontSize: 12, margin: "-8px 0 12px" }}>{err}</p>}
        <Btn fullWidth disabled={loading} onClick={mode === "admin" ? handleAdmin : handleUser}>{loading ? "Signing in…" : "Sign in"}</Btn>
      </Card>
    </div>
  );
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashTab, setDashTab] = useState("active");
  const [search, setSearch] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  const load = () => { setLoading(true); api.getDashboardStats().then(s => { setStats(s); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  if (loading) return <Loader />;
  if (!stats) return <EmptyState text="Could not load dashboard" />;

  const q = search.toLowerCase();
  const filteredActive = (stats.store_breakdown || []).filter(s =>
    !q || (s.location_name || s.location_id || "").toLowerCase().includes(q)
  );
  const filteredInactive = (stats.inactive_stores || []).filter(s =>
    !q || `${s.name} ${s.address || ""}`.toLowerCase().includes(q)
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div><h2 style={{ ...T.h1, marginBottom: 2 }}>Overview</h2><p style={{ ...T.small, margin: 0 }}>{formatDate(today)}</p></div>
        <Btn variant="ghost" size="sm" onClick={load}>Refresh</Btn>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 28 }}>
        {[
          { label: "Active Stores", value: `${stats.stores_active}/${stats.total_stores}`, sub: "today" },
          { label: "No Activity", value: `${stats.stores_inactive}/${stats.total_stores}`, sub: "stores" },
          { label: "Total Stores", value: stats.total_stores, sub: "registered" },
        ].map(c => (
          <Card key={c.label} pad={18}>
            <p style={{ ...T.label, marginBottom: 8 }}>{c.label}</p>
            <p style={{ fontSize: 32, fontWeight: 700, color: C.text, margin: "0 0 2px", lineHeight: 1 }}>{c.value}</p>
            <p style={{ ...T.small, margin: 0 }}>{c.sub}</p>
          </Card>
        ))}
        <Card pad={18}>
          <p style={{ ...T.label, marginBottom: 10 }}>Guards Present</p>
          <p style={{ fontSize: 32, fontWeight: 700, color: C.text, margin: "0 0 10px", lineHeight: 1 }}>{stats.guards_present}<span style={{ fontSize: 14, fontWeight: 400, color: C.textSub }}>/{stats.total_guards}</span></p>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, background: C.amberLight, borderRadius: 8, padding: "6px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.amber, letterSpacing: ".5px", marginBottom: 2 }}>☀ DAY</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.amber }}>{stats.total_day_shift || 0}</div>
            </div>
            <div style={{ flex: 1, background: C.accentMid, borderRadius: 8, padding: "6px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: ".5px", marginBottom: 2 }}>🌙 NIGHT</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.accent }}>{stats.total_night_shift || 0}</div>
            </div>
          </div>
        </Card>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[["active","Active"], ["inactive","No Attendance"]].map(([k, label]) => (
          <button key={k} onClick={() => setDashTab(k)}
            style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${dashTab === k ? C.accent : C.border}`, background: dashTab === k ? C.accentLight : C.surface, color: dashTab === k ? C.accent : C.textSub, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: font }}>
            {label} · {k === "active" ? stats.store_breakdown?.length || 0 : stats.stores_inactive}
          </button>
        ))}
      </div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search stores…" />
      {dashTab === "active" && (!filteredActive.length ? <EmptyState text={search ? "No stores match your search." : "No attendance recorded today yet."} /> : (
        <Card pad={0}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Store", "Day Shift", "Night Shift", "Last Check-in", "Status"].map(h => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left", ...T.label }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredActive.map((s, i) => {
                const t = new Date(s.last_checkin);
                const time = isNaN(t) ? s.last_checkin : t.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
                const dayExpected = s.day_shift_expected || 0;
                const nightExpected = s.night_shift_expected || 0;
                const dayActual = s.day_shift_actual || 0;
                const nightActual = s.night_shift_actual || 0;
                const dayColor = dayActual >= dayExpected ? C.green : C.amber;
                const nightColor = nightActual >= nightExpected ? C.green : C.accent;
                return (
                  <tr key={s.location_id} style={{ borderTop: i === 0 ? "none" : `1px solid ${C.borderLight}` }}>
                    <td style={{ padding: "12px 16px" }}>
                      <p style={{ fontWeight: 600, fontSize: 13, margin: "0 0 2px" }}>{s.location_name || s.location_id}</p>
                      <p style={{ ...T.small, margin: 0, color: C.textMute }}>{s.location_id}</p>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ background: C.amberLight, color: dayColor, fontWeight: 700, fontSize: 13, padding: "3px 10px", borderRadius: 20 }}>☀ {dayActual}/{dayExpected}</span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ background: C.accentMid, color: nightColor, fontWeight: 700, fontSize: 13, padding: "3px 10px", borderRadius: 20 }}>🌙 {nightActual}/{nightExpected}</span>
                    </td>
                    <td style={{ padding: "12px 16px", color: C.textMid }}>{time}</td>
                    <td style={{ padding: "12px 16px" }}><Badge color="green">Active</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ))}
      {dashTab === "inactive" && (!filteredInactive.length ? <EmptyState text={search ? "No stores match your search." : "All stores have check-ins today."} /> : (
        <Card pad={0}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{["Store","Address","Status"].map(h => <th key={h} style={{ padding: "11px 16px", textAlign: "left", ...T.label }}>{h}</th>)}</tr></thead>
            <tbody>
              {filteredInactive.map((s, i) => (
                <tr key={s.id} style={{ borderTop: i === 0 ? "none" : `1px solid ${C.borderLight}` }}>
                  <td style={{ padding: "12px 16px" }}><p style={{ fontWeight: 600, fontSize: 13, margin: "0 0 2px" }}>{s.name}</p><p style={{ ...T.small, margin: 0, color: C.textMute }}>{s.id}</p></td>
                  <td style={{ padding: "12px 16px", color: C.textSub }}>{s.address || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><Badge color="red">No Attendance</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
    </div>
  );
}

function AdminAnalytics() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shiftView, setShiftView] = useState("day");
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = (d) => {
    setLoading(true);
    setData(null);
    api.getVendorAnalytics(d)
      .then(r => { setData(r); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(date); }, [date]);

  const handleExportImage = async () => {
    if (!data) return;
    setExporting(true);
    const shift = shiftView;
    const presentKey = shift === "day" ? "day_present" : "night_present";
    const expectedKey = shift === "day" ? "day_expected" : "night_expected";
    const shiftColor = shift === "day" ? "#D97706" : "#2563EB";
    const shiftLabel = shift === "day" ? "☀ Day Shift" : "🌙 Night Shift";
    const totalPresent  = shift === "day" ? data.totals.day_present  : data.totals.night_present;
    const totalExpected = shift === "day" ? data.totals.day_expected : data.totals.night_expected;
    const totalSilent   = data.vendors.reduce((n, v) => n + v.stores.filter(s => s[presentKey] === 0 && s[expectedKey] > 0).length, 0);
    const totalActive   = data.vendors.reduce((n, v) => n + v.stores.filter(s => s[presentKey] > 0).length, 0);
    const totalMissing  = Math.max(0, totalExpected - totalPresent);

    const statusBadge = (present, expected) => {
      if (present === 0 && expected > 0) return `<span style="background:#FFFBEB;color:#D97706;font-size:11px;font-weight:600;padding:3px 8px;border-radius:20px;">No Attendance</span>`;
      if (present >= expected) return `<span style="background:#ECFDF5;color:#059669;font-size:11px;font-weight:600;padding:3px 8px;border-radius:20px;">Full</span>`;
      return `<span style="background:#FEF2F2;color:#DC2626;font-size:11px;font-weight:600;padding:3px 8px;border-radius:20px;">Partial</span>`;
    };
    const vendorRows = data.vendors.map(v => {
      const vPresent  = v[presentKey];
      const vExpected = v[expectedKey];
      if (vExpected === 0) return "";
      const vSilent = v.stores.filter(s => s[presentKey] === 0 && s[expectedKey] > 0).length;
      const storeRows = v.stores.filter(s => s[expectedKey] > 0).sort((a, b) => a[presentKey] - b[presentKey]).map(s => `
        <tr style="border-top:1px solid #F3F4F6;">
          <td style="padding:7px 14px 7px 28px;font-size:12px;color:#374151;">${s.name}</td>
          <td style="padding:7px 14px;font-size:12px;text-align:center;color:${shiftColor};font-weight:600;">${s[presentKey]}/${s[expectedKey]}</td>
          <td style="padding:7px 14px;text-align:center;">${statusBadge(s[presentKey], s[expectedKey])}</td>
        </tr>`).join("");
      return `
        <tr style="background:#F9FAFB;border-top:1px solid #E5E7EB;">
          <td style="padding:10px 14px;font-size:13px;font-weight:700;color:#111827;">${v.vendor_name}</td>
          <td style="padding:10px 14px;font-size:13px;text-align:center;color:${shiftColor};font-weight:700;">${vPresent}/${vExpected}</td>
          <td style="padding:10px 14px;font-size:13px;text-align:center;color:#D97706;font-weight:${vSilent > 0 ? 700 : 400};">${vSilent > 0 ? vSilent + " silent" : "—"}</td>
        </tr>${storeRows}`;
    }).join("");

    const html = `<div style="font-family:-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',sans-serif;background:#F9FAFB;padding:32px;min-width:760px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:24px;">
        <div><div style="font-size:22px;font-weight:700;color:#111827;">GuardTrack — ${shiftLabel}</div>
        <div style="font-size:13px;color:#6B7280;margin-top:2px;">${new Date(date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div></div>
        <div style="font-size:11px;color:#9CA3AF;">Generated ${new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
      </div>
      <div style="display:flex;gap:12px;margin-bottom:24px;">
        ${[
          { label: "Guards Present", value: `${totalPresent} / ${totalExpected}`, color: shiftColor },
          { label: "Missing Guards", value: totalMissing, color: "#DC2626" },
          { label: "Active Stores",  value: totalActive,  color: "#059669" },
          { label: "No Attendance",    value: totalSilent,  color: "#D97706" },
        ].map(c => `<div style="background:#fff;border:1px solid #E5E7EB;border-radius:10px;padding:14px 18px;min-width:130px;">
          <div style="font-size:10px;font-weight:600;color:#6B7280;text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px;">${c.label}</div>
          <div style="font-size:24px;font-weight:700;color:${c.color};line-height:1;">${c.value}</div>
        </div>`).join("")}
      </div>
      <div style="background:#fff;border:1px solid #E5E7EB;border-radius:12px;overflow:hidden;">
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr style="border-bottom:1px solid #E5E7EB;">
            <th style="padding:11px 14px;text-align:left;font-size:10px;font-weight:600;color:#6B7280;text-transform:uppercase;letter-spacing:.6px;">Vendor / Store</th>
            <th style="padding:11px 14px;text-align:center;font-size:10px;font-weight:600;color:#6B7280;text-transform:uppercase;letter-spacing:.6px;">Present / Expected</th>
            <th style="padding:11px 14px;text-align:center;font-size:10px;font-weight:600;color:#6B7280;text-transform:uppercase;letter-spacing:.6px;">Status</th>
          </tr></thead>
          <tbody>${vendorRows}
            <tr style="border-top:2px solid #E5E7EB;background:#F9FAFB;">
              <td style="padding:12px 14px;font-size:13px;font-weight:700;color:#111827;">TOTAL</td>
              <td style="padding:12px 14px;font-size:13px;font-weight:700;color:${shiftColor};text-align:center;">${totalPresent}/${totalExpected}</td>
              <td style="padding:12px 14px;font-size:13px;font-weight:700;color:#DC2626;text-align:center;">${totalMissing > 0 ? totalMissing + " missing" : "✓ Full"}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>`;

    try {
      await new Promise((resolve, reject) => {
        if (window.html2canvas) { resolve(); return; }
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
        script.onload = resolve; script.onerror = reject;
        document.head.appendChild(script);
      });
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px;top:0;z-index:-1;";
      container.innerHTML = html;
      document.body.appendChild(container);
      const canvas = await window.html2canvas(container.firstElementChild, { backgroundColor: "#F9FAFB", scale: 2, useCORS: true, logging: false });
      document.body.removeChild(container);
      const link = document.createElement("a");
      link.download = `guardtrack-${shift}shift-${date}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch { alert("Export failed. Please try again."); }
    setExporting(false);
  };

  const thStyle = { padding: "11px 16px", textAlign: "center", ...T.label };
  const tdCenter = { padding: "12px 16px", fontSize: 13, textAlign: "center" };
  const presentKey  = shiftView === "day" ? "day_present"  : "night_present";
  const expectedKey = shiftView === "day" ? "day_expected" : "night_expected";
  const shiftColor  = shiftView === "day" ? C.amber : C.accent;
  const totalPresent  = data ? (shiftView === "day" ? data.totals.day_present  : data.totals.night_present)  : 0;
  const totalExpected = data ? (shiftView === "day" ? data.totals.day_expected : data.totals.night_expected) : 0;
  const totalSilent   = data ? data.vendors.reduce((n, v) => n + v.stores.filter(s => s[presentKey] === 0 && s[expectedKey] > 0).length, 0) : 0;
  const totalActive   = data ? data.vendors.reduce((n, v) => n + v.stores.filter(s => s[presentKey] > 0).length, 0) : 0;
  const totalMissing  = Math.max(0, totalExpected - totalPresent);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div><h2 style={{ ...T.h1, marginBottom: 2 }}>Analytics</h2><p style={{ ...T.small, margin: 0 }}>Vendor-wise breakdown</p></div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input type="date" value={date} max={today}
            onChange={e => { if (e.target.value) { setDate(e.target.value); setExpandedVendor(null); } }}
            style={{ padding: "7px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.text, fontFamily: font, outline: "none", background: C.surface, cursor: "pointer" }} />
          <Btn variant="ghost" size="sm" onClick={() => load(date)}>Refresh</Btn>
          {data && <Btn variant="ghost" size="sm" disabled={exporting} onClick={handleExportImage}>{exporting ? "Exporting…" : "Export Image"}</Btn>}
        </div>
      </div>

      <div style={{ display: "flex", background: C.bg, borderRadius: 10, padding: 4, marginBottom: 20, width: "fit-content", border: `1px solid ${C.border}` }}>
        {[["day", "☀ Day Shift"], ["night", "🌙 Night Shift"]].map(([k, label]) => (
          <button key={k} onClick={() => { setShiftView(k); setExpandedVendor(null); }}
            style={{ padding: "8px 24px", border: "none", borderRadius: 7, cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: font, transition: "all .15s", background: shiftView === k ? C.surface : "transparent", color: shiftView === k ? (k === "day" ? C.amber : C.accent) : C.textSub, boxShadow: shiftView === k ? "0 1px 4px rgba(0,0,0,.1)" : "none" }}>
            {label}
          </button>
        ))}
      </div>

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
          <Card pad={18} style={{ borderLeft: `4px solid ${shiftColor}` }}>
            <p style={{ ...T.label, marginBottom: 10, color: shiftColor }}>
              {shiftView === "day" ? "☀ Day Shift Guards Present" : "🌙 Night Shift Guards Present"}
            </p>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 42, fontWeight: 800, color: C.text, lineHeight: 1 }}>{totalPresent}</span>
              <span style={{ fontSize: 20, color: C.textMute, marginBottom: 6 }}>/ {totalExpected}</span>
            </div>
            <div style={{ height: 6, background: C.borderLight, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${totalExpected ? Math.min(100, (totalPresent / totalExpected) * 100) : 0}%`, background: totalPresent >= totalExpected ? C.green : shiftColor, borderRadius: 4, transition: "width .4s" }} />
            </div>
            <p style={{ ...T.small, margin: "6px 0 0", color: totalPresent >= totalExpected ? C.green : shiftColor }}>
              {totalMissing > 0 ? `${totalMissing} guards absent` : "All present ✓"}
            </p>
          </Card>
          <Card pad={16}>
            <p style={{ ...T.label, marginBottom: 6 }}>Missing Guards</p>
            <p style={{ fontSize: 32, fontWeight: 800, color: totalMissing > 0 ? C.red : C.green, margin: 0, lineHeight: 1 }}>{totalMissing}</p>
            <p style={{ ...T.small, margin: "4px 0 0" }}>of {totalExpected} expected</p>
          </Card>
          <Card pad={16}>
            <p style={{ ...T.label, marginBottom: 6 }}>Stores with Guards</p>
            <p style={{ fontSize: 32, fontWeight: 800, color: C.green, margin: 0, lineHeight: 1 }}>{totalActive}</p>
            <p style={{ ...T.small, margin: "4px 0 0" }}>of {data.totals.store_count} stores</p>
          </Card>
          <Card pad={16}>
            <p style={{ ...T.label, marginBottom: 6 }}>No Guards</p>
            <p style={{ fontSize: 32, fontWeight: 800, color: totalSilent > 0 ? C.amber : C.green, margin: 0, lineHeight: 1 }}>{totalSilent}</p>
            <p style={{ ...T.small, margin: "4px 0 0" }}>Stores with No Attendance</p>
          </Card>
        </div>
      )}

      {loading && <Loader text="Loading analytics…" />}
      {!loading && !data && <EmptyState text="Could not load analytics." />}
      {!loading && data && data.vendors.length === 0 && <EmptyState text="No store data found." />}

      {!loading && data && data.vendors.length > 0 && (
        <Card pad={0}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                <th style={{ ...thStyle, textAlign: "left" }}>Vendor</th>
                <th style={{ ...thStyle, color: shiftColor }}>Present / Expected</th>
                <th style={thStyle}>Missing</th>
                <th style={thStyle}>No Guards</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.vendors.map((v, i) => {
                const vPresent  = v[presentKey];
                const vExpected = v[expectedKey];
                if (vExpected === 0) return null;
                const vMissing = Math.max(0, vExpected - vPresent);
                const vSilent  = v.stores.filter(s => s[presentKey] === 0 && s[expectedKey] > 0).length;
                const expanded = expandedVendor === v.vendor_name;
                const allGood  = vMissing === 0 && vSilent === 0;
                return (
                  <React.Fragment key={v.vendor_name}>
                    <tr onClick={() => setExpandedVendor(expanded ? null : v.vendor_name)}
                      style={{ borderTop: i === 0 ? "none" : `1px solid ${C.borderLight}`, cursor: "pointer", background: expanded ? C.bg : "transparent" }}
                      onMouseEnter={e => e.currentTarget.style.background = C.bg}
                      onMouseLeave={e => e.currentTarget.style.background = expanded ? C.bg : "transparent"}>
                      <td style={{ padding: "12px 16px", fontWeight: 600 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 11, color: C.textMute }}>{expanded ? "▲" : "▼"}</span>
                          {v.vendor_name}
                          {allGood && <Badge color="green">Full</Badge>}
                        </div>
                      </td>
                      <td style={{ ...tdCenter, fontWeight: 700, color: vPresent >= vExpected ? C.green : shiftColor }}>{vPresent}/{vExpected}</td>
                      <td style={{ ...tdCenter, fontWeight: vMissing > 0 ? 700 : 400, color: vMissing > 0 ? C.red : C.textMute }}>{vMissing > 0 ? vMissing : "—"}</td>
                      <td style={{ ...tdCenter, fontWeight: vSilent > 0 ? 700 : 400, color: vSilent > 0 ? C.amber : C.textMute }}>{vSilent > 0 ? vSilent : "—"}</td>
                      <td style={{ ...tdCenter }}>
                        {allGood ? <Badge color="green">Full</Badge> : vSilent > 0 ? <Badge color="amber">Partial</Badge> : <Badge color="red">Short</Badge>}
                      </td>
                    </tr>
                    {expanded && (
                      <tr style={{ borderTop: `1px solid ${C.borderLight}` }}>
                        <td colSpan={5} style={{ padding: "0 0 8px 0", background: C.bg }}>
                          <div style={{ margin: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`, overflow: "hidden", background: C.surface }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${C.borderLight}`, background: C.bg }}>
                                  {["Store", "Present / Expected", "Status"].map(h => (
                                    <th key={h} style={{ padding: "8px 14px", textAlign: h === "Store" ? "left" : "center", ...T.label, fontSize: 10 }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {v.stores.filter(s => s[expectedKey] > 0).sort((a, b) => a[presentKey] - b[presentKey]).map((s, si) => (
                                  <tr key={s.id} style={{ borderTop: si === 0 ? "none" : `1px solid ${C.borderLight}` }}>
                                    <td style={{ padding: "8px 14px", fontWeight: 500 }}>{s.name}</td>
                                    <td style={{ padding: "8px 14px", textAlign: "center", fontWeight: 600, color: s[presentKey] >= s[expectedKey] ? C.green : s[presentKey] > 0 ? shiftColor : C.textMute }}>
                                      {s[presentKey]}/{s[expectedKey]}
                                    </td>
                                    <td style={{ padding: "8px 14px", textAlign: "center" }}>
                                      {s[presentKey] === 0 ? <Badge color="amber">No Guards</Badge>
                                        : s[presentKey] >= s[expectedKey] ? <Badge color="green">Full</Badge>
                                        : <Badge color="red">Partial</Badge>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              <tr style={{ borderTop: `2px solid ${C.border}`, background: C.bg }}>
                <td style={{ padding: "12px 16px", fontWeight: 700 }}>Total</td>
                <td style={{ ...tdCenter, fontWeight: 700, color: totalPresent >= totalExpected ? C.green : shiftColor }}>{totalPresent}/{totalExpected}</td>
                <td style={{ ...tdCenter, fontWeight: 700, color: totalMissing > 0 ? C.red : C.textMute }}>{totalMissing > 0 ? totalMissing : "—"}</td>
                <td style={{ ...tdCenter, fontWeight: 700, color: totalSilent > 0 ? C.amber : C.textMute }}>{totalSilent > 0 ? totalSilent : "—"}</td>
                <td style={{ ...tdCenter }}></td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function AdminAttendanceList() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => { api.getStoreStats().then(s => { setStats(s); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const filtered = stats.filter(s =>
    !search || `${s.name} ${s.address || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Loader />;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={T.h1}>Attendance</h2>
        <Btn variant="ghost" size="sm" onClick={() => api.exportAttendanceCsv({})}>Export CSV</Btn>
      </div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search stores…" />
      <Card pad={0}>
        {!filtered.length ? <EmptyState text={search ? "No stores match your search." : "No stores yet"} /> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{["Store","Address","Today","Total"].map(h => <th key={h} style={{ padding: "11px 16px", textAlign: "left", ...T.label }}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={s.id} onClick={() => navigate(`/admin/store/${s.id}`)} style={{ borderTop: i === 0 ? "none" : `1px solid ${C.borderLight}`, cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = C.bg} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "12px 16px", fontWeight: 600 }}>{s.name}</td>
                  <td style={{ padding: "12px 16px", color: C.textSub }}>{s.address || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ fontWeight: 700, color: s.today_count > 0 ? C.green : C.textMute }}>{s.today_count}</span></td>
                  <td style={{ padding: "12px 16px", color: C.textSub }}>{s.total_records}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function AdminStorePage() {
  const { storeId } = useParams();
  const [days, setDays] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => { Promise.all([api.getStore(storeId), api.getStoreDays(storeId)]).then(([s, d]) => { setStore(s); setDays(d); setLoading(false); }).catch(() => setLoading(false)); }, [storeId]);

  const months = [...new Set(days.map(d => d.day.slice(0, 7)))].sort((a, b) => b.localeCompare(a));
  const getDaysInMonth = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    const count = new Date(y, m, 0).getDate();
    const result = [];
    for (let i = 1; i <= count; i++) result.push(`${ym}-${String(i).padStart(2, "0")}`);
    return result;
  };
  const activityMap = Object.fromEntries(days.map(d => [d.day, d]));
  const allDaysInMonth = getDaysInMonth(selectedMonth).filter(d => d <= today).reverse();

  if (loading) return <Shell bg={C.bg}><AdminHeader /><Loader /></Shell>;
  return (
    <Shell bg={C.bg}>
      <AdminHeader />
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 24 }}>
        <Link to="/admin" style={{ fontSize: 13, color: C.accent, textDecoration: "none", fontWeight: 500 }}>← All Stores</Link>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "16px 0 20px" }}>
          <div><h2 style={{ ...T.h1, marginBottom: 2 }}>{store?.name}</h2><p style={{ ...T.small, margin: 0 }}>{store?.address}</p></div>
          <Btn variant="ghost" size="sm" onClick={() => api.exportAttendanceCsv({ store: storeId })}>Export CSV</Btn>
        </div>
        {months.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {months.map(m => {
              const label = new Date(m + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" });
              const active = selectedMonth === m;
              return (
                <button key={m} onClick={() => setSelectedMonth(m)}
                  style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.accentLight : C.surface, color: active ? C.accent : C.textSub, fontSize: 13, fontWeight: active ? 600 : 400, cursor: "pointer", fontFamily: font }}>
                  {label}
                </button>
              );
            })}
          </div>
        )}
        {!allDaysInMonth.length ? <EmptyState text="No dates to show yet." /> : (
          <Card pad={0}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{["Date","Guards","Check-ins",""].map(h => <th key={h} style={{ padding: "11px 16px", textAlign: "left", ...T.label }}>{h}</th>)}</tr></thead>
              <tbody>
                {allDaysInMonth.map((dateStr, i) => {
                  const isToday = dateStr === today;
                  const d = activityMap[dateStr];
                  const hasActivity = !!d;
                  return (
                    <tr key={dateStr}
                      onClick={() => hasActivity && navigate(`/admin/store/${storeId}/${dateStr}`)}
                      style={{ borderTop: i === 0 ? "none" : `1px solid ${C.borderLight}`, cursor: hasActivity ? "pointer" : "default", background: hasActivity ? "transparent" : C.bg }}
                      onMouseEnter={e => { if (hasActivity) e.currentTarget.style.background = C.bg; }}
                      onMouseLeave={e => { e.currentTarget.style.background = hasActivity ? "transparent" : C.bg; }}>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontWeight: 500, color: hasActivity ? C.text : C.textMute }}>{formatDate(dateStr)}</span>
                        {isToday && <span style={{ marginLeft: 8 }}><Badge color="blue">Today</Badge></span>}
                      </td>
                      <td style={{ padding: "12px 16px", color: hasActivity ? C.textMid : C.textMute }}>{hasActivity ? d.guard_count : "—"}</td>
                      <td style={{ padding: "12px 16px", color: hasActivity ? C.textMid : C.textMute }}>{hasActivity ? d.checkin_count : "—"}</td>
                      <td style={{ padding: "12px 16px" }}>{!hasActivity && <Badge color="red">No Activity</Badge>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </Shell>
  );
}

function AdminDayPage() {
  const { storeId, day } = useParams();
  const [records, setRecords] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewSelfie, setViewSelfie] = useState(null);
  const [search, setSearch] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const isToday = day === today;

  useEffect(() => { Promise.all([api.getStore(storeId), api.getAttendance({ store: storeId, date: day })]).then(([s, recs]) => { setStore(s); setRecords(recs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))); setLoading(false); }).catch(() => setLoading(false)); }, [storeId, day]);

  const filteredRecords = records.filter(r =>
    !search || `${r.guard_name} ${r.guard_mobile || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Shell bg={C.bg}><AdminHeader /><Loader /></Shell>;
  return (
    <Shell bg={C.bg}>
      <AdminHeader />
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 24 }}>
        <Link to={`/admin/store/${storeId}`} style={{ fontSize: 13, color: C.accent, textDecoration: "none", fontWeight: 500 }}>← {store?.name}</Link>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "16px 0 20px" }}>
          <div><h2 style={{ ...T.h1, marginBottom: 2 }}>{formatDate(day)} {isToday && <Badge color="blue">Today</Badge>}</h2><p style={{ ...T.small, margin: 0 }}>{records.length} check-in{records.length !== 1 ? "s" : ""}</p></div>
          <Btn variant="ghost" size="sm" onClick={() => api.exportAttendanceCsv({ store: storeId, date: day })}>Export CSV</Btn>
        </div>
        <SearchBar value={search} onChange={setSearch} placeholder="Search by guard name or mobile…" />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filteredRecords.length === 0 && <EmptyState text={search ? "No guards match your search." : "No records for this day."} />}
          {filteredRecords.map(r => {
            const noCheckout = !r.checkout_time;
            return (
              <Card key={r.id} pad={16} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <SelfieThumb recordId={r.id} selfieUrl={r.selfie_url} onClick={() => setViewSelfie(r)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontWeight: 600, fontSize: 14, margin: "0 0 2px" }}>{r.guard_name}</p>
                  {r.guard_mobile && <p style={{ ...T.small, margin: "0 0 3px", color: C.textMute }}>{r.guard_mobile}</p>}
                  {r.shift && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: r.shift === "day" ? C.amberLight : C.accentMid, color: r.shift === "day" ? C.amber : C.accent }}>{r.shift === "day" ? "☀ Day Shift" : "🌙 Night Shift"}</span>}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: C.textSub, flexWrap: "wrap" }}>
                    <span>In: <strong>{new Date(r.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</strong></span>
                    {r.checkout_time && <span>Out: <strong style={{ color: C.green }}>{new Date(r.checkout_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</strong></span>}
                    {r.working_hours && <span style={{ color: C.accent, fontWeight: 600 }}>{r.working_hours} hrs</span>}
                    {noCheckout && <span style={{ color: C.textMute }}>On shift</span>}
                  </div>
                </div>
                <div style={{ textAlign: "right", fontSize: 12, color: C.textSub, flexShrink: 0 }}>
                  <span style={{ color: C.green, fontWeight: 600 }}>{r.distance}m</span>
                  {r.accuracy && <div style={{ color: C.textMute }}>±{r.accuracy}m</div>}
                </div>
              </Card>
            );
          })}
        </div>
        {viewSelfie && (
          <div onClick={() => setViewSelfie(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            {viewSelfie.selfie_url ? <img src={viewSelfie.selfie_url} alt="" style={{ maxWidth: "90%", maxHeight: "80vh", borderRadius: 12 }} /> : <Spinner />}
          </div>
        )}
      </div>
    </Shell>
  );
}

function AdminLocationsList({ readOnly = false }) {
  const [locs, setLocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formTab, setFormTab] = useState("basic");
  const [form, setForm] = useState({ name: "", address: "", lat: "", lng: "", radius: 150, day_shift_count: 0, night_shift_count: 0, vendor_name: "", area_manager_name: "", area_manager_phone: "", zippee_poc_name: "" });
  const [copied, setCopied] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => { api.getLocations().then(setLocs).finally(() => setLoading(false)); };
  useEffect(load, []);
  const baseUrl = window.location.origin + "/s/";

  const openAdd = () => {
    setForm({ name: "", address: "", lat: "", lng: "", radius: 150, day_shift_count: 0, night_shift_count: 0, vendor_name: "", area_manager_name: "", area_manager_phone: "", zippee_poc_name: "" });
    setEditId(null); setFormTab("basic"); setShowForm(true);
  };
  const openEdit = (loc) => {
    setForm({
      name: loc.name, address: loc.address || "", lat: String(loc.lat), lng: String(loc.lng),
      radius: loc.radius, day_shift_count: loc.day_shift_count || 0, night_shift_count: loc.night_shift_count || 0,
      vendor_name: loc.vendor_name || "", area_manager_name: loc.area_manager_name || "",
      area_manager_phone: loc.area_manager_phone || "", zippee_poc_name: loc.zippee_poc_name || ""
    });
    setEditId(loc.id); setFormTab("basic"); setShowForm(true);
  };
  const save = async () => {
    const dayShift = Number(form.day_shift_count) || 0;
    const nightShift = Number(form.night_shift_count) || 0;
    const payload = {
      name: form.name, address: form.address,
      lat: parseFloat(form.lat), lng: parseFloat(form.lng), radius: Number(form.radius),
      guard_count: dayShift + nightShift, day_shift_count: dayShift, night_shift_count: nightShift,
      vendor_name: form.vendor_name, area_manager_name: form.area_manager_name,
      area_manager_phone: form.area_manager_phone, zippee_poc_name: form.zippee_poc_name
    };
    if (!payload.name || isNaN(payload.lat) || isNaN(payload.lng)) return;
    if (editId) await api.updateLocation({ id: editId, ...payload }); else await api.createLocation(payload);
    setShowForm(false); load();
  };
  const del = async (id) => { if (!window.confirm("Delete this store?")) return; await api.deleteLocation(id); load(); };
  const copyLink = (id) => { navigator.clipboard?.writeText(baseUrl + id); setCopied(id); setTimeout(() => setCopied(null), 2000); };

  const filteredLocs = locs.filter(l =>
    !search || `${l.name} ${l.address || ""} ${l.vendor_name || ""} ${l.area_manager_name || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Loader />;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div><h2 style={T.h1}>Locations</h2><p style={{ ...T.small, margin: "2px 0 0" }}>{locs.length} stores</p></div>
        {!readOnly && <Btn size="sm" onClick={openAdd}>+ Add Store</Btn>}
      </div>
      {!readOnly && showForm && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ ...T.h3, marginBottom: 14 }}>{editId ? "Edit" : "Add"} Location</h3>
          <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, marginBottom: 16 }}>
            {[["basic","Store Details"], ["contacts","Contacts"]].map(([k, label]) => (
              <button key={k} onClick={() => setFormTab(k)}
                style={{ padding: "8px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: formTab === k ? 600 : 400, color: formTab === k ? C.accent : C.textSub, borderBottom: `2px solid ${formTab === k ? C.accent : "transparent"}`, marginBottom: -1, fontFamily: font }}>
                {label}
              </button>
            ))}
          </div>
          {formTab === "basic" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "span 2" }}><Input label="Store Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="Dark Store — Sector 18" /></div>
              <div style={{ gridColumn: "span 2" }}><Input label="Address" value={form.address} onChange={v => setForm(f => ({ ...f, address: v }))} placeholder="Area, City" /></div>
              <Input label="Latitude" value={form.lat} onChange={v => setForm(f => ({ ...f, lat: v }))} placeholder="28.5355" />
              <Input label="Longitude" value={form.lng} onChange={v => setForm(f => ({ ...f, lng: v }))} placeholder="77.3910" />
              <div><Input label="☀ Day Shift Guards" value={String(form.day_shift_count)} onChange={v => setForm(f => ({ ...f, day_shift_count: v.replace(/\D/g, "") || "0" }))} placeholder="e.g. 1" type="number" /></div>
              <div><Input label="🌙 Night Shift Guards" value={String(form.night_shift_count)} onChange={v => setForm(f => ({ ...f, night_shift_count: v.replace(/\D/g, "") || "0" }))} placeholder="e.g. 1" type="number" /></div>
              <div style={{ gridColumn: "span 2", background: C.bg, borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ ...T.label }}>Total Guards (auto-calculated)</span>
                <span style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{(Number(form.day_shift_count) || 0) + (Number(form.night_shift_count) || 0)}</span>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ ...T.label, display: "block", marginBottom: 6 }}>Geofence Radius: {form.radius}m</label>
                <input type="range" min={25} max={500} step={25} value={form.radius} onChange={e => setForm(f => ({ ...f, radius: Number(e.target.value) }))} style={{ width: "100%", accentColor: C.accent }} />
                <div style={{ display: "flex", justifyContent: "space-between", ...T.small }}><span>25m</span><span>500m</span></div>
              </div>
            </div>
          )}
          {formTab === "contacts" && (
            <div>
              <Input label="Vendor Name" value={form.vendor_name} onChange={v => setForm(f => ({ ...f, vendor_name: v }))} placeholder="e.g. Acme Security Pvt Ltd" />
              <Input label="Area Manager Name" value={form.area_manager_name} onChange={v => setForm(f => ({ ...f, area_manager_name: v }))} placeholder="e.g. Suresh Sharma" />
              <Input label="Area Manager Phone" value={form.area_manager_phone} onChange={v => setForm(f => ({ ...f, area_manager_phone: v }))} placeholder="e.g. 9818255272" type="tel" />
              <Input label="Zippee POC Name" value={form.zippee_poc_name} onChange={v => setForm(f => ({ ...f, zippee_poc_name: v }))} placeholder="e.g. Rahul Verma" />
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Btn onClick={save}>Save</Btn>
            <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
          </div>
        </Card>
      )}
      <SearchBar value={search} onChange={setSearch} placeholder="Search by name, address, vendor…" />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filteredLocs.length === 0 && <EmptyState text={search ? "No locations match your search." : "No stores added yet."} />}
        {filteredLocs.map(loc => {
          const expanded = expandedId === loc.id;
          const hasContacts = loc.vendor_name || loc.area_manager_name || loc.zippee_poc_name;
          const dayShift = loc.day_shift_count || 0;
          const nightShift = loc.night_shift_count || 0;
          const totalGuards = dayShift + nightShift || loc.guard_count || 0;
          return (
            <Card key={loc.id} pad={0}>
              <div style={{ padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontWeight: 600, fontSize: 14, margin: "0 0 2px" }}>{loc.name}</p>
                  <p style={{ ...T.small, margin: 0 }}>
                    {loc.address} · {loc.radius}m fence ·{" "}
                    <span style={{ color: C.amber }}>☀ {dayShift}</span>{" + "}
                    <span style={{ color: C.accent }}>🌙 {nightShift}</span>{" = "}
                    <strong>{totalGuards}</strong> guard{totalGuards !== 1 ? "s" : ""}
                  </p>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                  {hasContacts && <button onClick={() => setExpandedId(expanded ? null : loc.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: C.accent, fontWeight: 500, fontFamily: font, padding: "4px 8px" }}>{expanded ? "Hide" : "Details"}</button>}
                  {!readOnly && <><Btn variant="ghost" size="sm" onClick={() => openEdit(loc)}>Edit</Btn><Btn variant="ghost" size="sm" onClick={() => del(loc.id)} style={{ color: C.red }}>Delete</Btn></>}
                </div>
              </div>
              {expanded && hasContacts && (
                <div style={{ borderTop: `1px solid ${C.borderLight}`, padding: "14px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                  {loc.vendor_name && <div><p style={{ ...T.label, marginBottom: 3 }}>Vendor</p><p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: C.textMid }}>{loc.vendor_name}</p></div>}
                  {loc.area_manager_name && <div><p style={{ ...T.label, marginBottom: 3 }}>Area Manager</p><p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: C.textMid }}>{loc.area_manager_name}</p>{loc.area_manager_phone && <a href={`tel:${loc.area_manager_phone}`} style={{ fontSize: 12, color: C.accent, textDecoration: "none" }}>{loc.area_manager_phone}</a>}</div>}
                  {loc.zippee_poc_name && <div><p style={{ ...T.label, marginBottom: 3 }}>Zippee POC</p><p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: C.textMid }}>{loc.zippee_poc_name}</p></div>}
                </div>
              )}
              <div style={{ borderTop: `1px solid ${C.borderLight}`, padding: "10px 16px", display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: "0 0 12px 12px" }}>
                <code style={{ fontSize: 11, color: C.textSub, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{baseUrl}{loc.id}</code>
                <Btn variant="ghost" size="sm" onClick={() => copyLink(loc.id)} style={{ flexShrink: 0, color: copied === loc.id ? C.green : C.textMid }}>{copied === loc.id ? "Copied!" : "Copy"}</Btn>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function AdminUserAccess() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [formErr, setFormErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => { api.getStoreUsers().then(u => { setUsers(u); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setFormErr("");
    if (!form.email || !form.password) { setFormErr("All fields are required"); return; }
    setSaving(true);
    try { await api.createStoreUser({ email: form.email, password: form.password, location_id: "all", location_name: "All Stores" }); setShowForm(false); setForm({ email: "", password: "" }); load(); }
    catch (e) { setFormErr(e.message || "Error adding user"); }
    setSaving(false);
  };
  const del = async (id) => { if (!window.confirm("Remove access?")) return; await api.deleteStoreUser(id); load(); };

  if (loading) return <Loader />;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div><h2 style={T.h1}>User Access</h2><p style={{ ...T.small, margin: "2px 0 0" }}>Users can view all stores. Cannot add or edit locations.</p></div>
        <Btn size="sm" onClick={() => { setShowForm(true); setFormErr(""); }}>+ Add User</Btn>
      </div>
      {showForm && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ ...T.h3, marginBottom: 16 }}>Add User</h3>
          <Input label="Email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="user@company.com" type="email" />
          <Input label="Password" value={form.password} onChange={v => setForm(f => ({ ...f, password: v }))} placeholder="Set a password" />
          {formErr && <p style={{ color: C.red, fontSize: 12, margin: "-8px 0 12px" }}>{formErr}</p>}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn disabled={saving} onClick={save}>{saving ? "Saving…" : "Add User"}</Btn>
            <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
          </div>
        </Card>
      )}
      {!users.length && !showForm ? <EmptyState text="No users added yet." /> : (
        <Card pad={0}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{["Email","Access","Created",""].map((h, i) => <th key={i} style={{ padding: "11px 16px", textAlign: "left", ...T.label }}>{h}</th>)}</tr></thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={{ borderTop: i === 0 ? "none" : `1px solid ${C.borderLight}` }}>
                  <td style={{ padding: "12px 16px", fontWeight: 500 }}>{u.email}</td>
                  <td style={{ padding: "12px 16px" }}><Badge color="blue">All Stores</Badge></td>
                  <td style={{ padding: "12px 16px", color: C.textSub }}>{u.created_at?.slice(0, 10) || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><Btn variant="ghost" size="sm" onClick={() => del(u.id)} style={{ color: C.red }}>Remove</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function SelfieThumb({ recordId, selfieUrl, onClick }) {
  const [src, setSrc] = useState(selfieUrl || null);
  useEffect(() => { if (!selfieUrl && recordId) api.getSelfie(recordId).then(d => d?.selfie && setSrc(d.selfie)).catch(() => {}); }, [recordId, selfieUrl]);
  if (!src) return <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${C.border}` }}><UserIcon size={16} color={C.textMute} /></div>;
  return <img src={src} alt="" onClick={onClick} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: `1px solid ${C.border}`, cursor: "pointer", flexShrink: 0 }} />;
}

function CheckIcon({ size = 18, color = "currentColor" }) { return <svg width={size} height={size} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>; }
function XIcon({ size = 18, color = "currentColor" }) { return <svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function ClockIcon({ size = 18, color = "currentColor" }) { return <svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>; }
function AlertIcon({ size = 18, color = "currentColor" }) { return <svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function UserIcon({ size = 18, color = "currentColor" }) { return <svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>; }

function formatDate(d) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
